import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { motion } from "framer-motion";

function LogoSVG(): any {
    return (
        <svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="7.16272" height="7.16272" transform="matrix(0.705409 0.708801 -0.705409 0.708801 5.05273 0)" fill="#3162B4" />
            <rect width="7.16272" height="7.16272" transform="matrix(0.705409 0.708801 -0.705409 0.708801 10.9473 5.9231)" fill="#5215A3" />
            <rect width="7.16272" height="7.16272" transform="matrix(0.705409 0.708801 -0.705409 0.708801 5.05273 11.8462)" fill="#3A7575" />
        </svg>
    );
}

const GRADIENT_COLORS = "linear-gradient(45deg, #0284C7, #5EEAD4, #6D28D9, #4F46E5, #0284C7)";

interface KimaruButtonProps {
    animated?: boolean;
    className?: string;
    isKimaru?: boolean;
    onClick?: any;
    [key: string]: any;
}

interface KimaruButtonState {
    isHovered: any;
}

export default function KimaruButton({ animated = false, className, ...props }: KimaruButtonProps) {
    const [isHovered, setIsHovered] = React.useState(false);
    const { isKimaru = false } = props;

    const baseClasses = clsx(
        "inline-flex items-center justify-center transition-colors duration-200 shadow-sm",
        className
    );

    const defaultStyle = "gap-2 w-10 h-10 px-2 py-2 rounded-md text-sm font-medium bg-[#F1F5F9] text-gray-900 hover:bg-gray-100";
    const kimaruActiveClasses = "gap-0.5 bg-teal-600 text-white w-10 h-10 rounded-md";

    const button = (
        <button
            className={baseClasses + (isKimaru ? " " + kimaruActiveClasses : " " + defaultStyle)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={isKimaru}
            {...props}
        >
            {isKimaru ? (
                <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#F8FAFC" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>

                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 11 15" fill="none" className="flex-shrink-0">
                        <rect x="3.41055" width="4.82324" height="4.82324" transform="rotate(45 3.41055 0)" fill="white"/>
                        <rect x="7.3895" y="3.97888" width="4.82324" height="4.82324" transform="rotate(45 7.3895 3.97888)" fill="white"/>
                        <rect x="3.41055" y="7.95789" width="4.82324" height="4.82324" transform="rotate(45 3.41055 7.95789)" fill="white"/>
                    </svg>
                </>
            ) : (
                <LogoSVG />
            )}
        </button>
    );

    if (!animated) return button;

    const sharedGradientStyle: any = {
        background: GRADIENT_COLORS,
        backgroundSize: "300% 300%",
    };

    return (
        <motion.div
            className="inline-block relative"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
            {/* Outer blur effect */}
            <motion.div
                className="absolute -inset-1 rounded-lg blur-sm z-0"
                style={sharedGradientStyle}
                animate={{
                    backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
                    opacity: isHovered ? 0.7 : 0.4,
                }}
                transition={{
                    backgroundPosition: {
                        duration: isHovered ? 3 : 8,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                    opacity: { duration: 0.3, ease: "easeOut" },
                }}
            />

            {/* Inner border effect */}
            <motion.div
                className="absolute -inset-0.5 rounded-md z-0"
                style={sharedGradientStyle}
                animate={{
                    backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
                    filter: isHovered ? "brightness(1.2)" : "brightness(1)",
                }}
                transition={{
                    backgroundPosition: {
                        duration: isHovered ? 2 : 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                    filter: { duration: 0.3, ease: "easeOut" },
                }}
            />

            <div className="relative z-10">{button}</div>
        </motion.div>
    );
}

KimaruButton.propTypes = {
    animated: PropTypes.bool,
    className: PropTypes.string,
    isKimaru: PropTypes.bool,
    onClick: PropTypes.func,
};
